import useActiveWeb3React from "hooks/useActiveWeb3React";
import { useCallback, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { TransactionResponse } from "@ethersproject/providers";
import { Currency, CurrencyAmount, Percent } from "@uniswap/sdk-core";

import { ButtonText } from "../../components/Button";
import { AutoColumn } from "../../components/Column";
import CurrencyInputPanel from "../../components/CurrencyInputPanel";
import { AddRemoveTabs } from "../../components/NavigationTabs";
import Row from "../../components/Row";
import TransactionConfirmationModal, { ConfirmationModalContent } from "../../components/TransactionConfirmationModal";
import { ASSET_VAULT_ADDRESSES } from "../../constants/addresses";
import { useCurrency } from "../../hooks/Tokens";
import {
  ApprovalState,
  useApproveCallback
} from "../../hooks/useApproveCallback";
import { useAssetVaultContract } from "../../hooks/useContract";
import { useUSDCValue } from "../../hooks/useUSDCPrice";
import { useWalletModalToggle } from "../../state/application/hooks";
import { Field } from "../../state/mint/v3/actions";
import { TransactionType } from "../../state/transactions/actions";
import { useTransactionAdder } from "../../state/transactions/hooks";
import { ThemedText } from "../../theme";
import { currencyId } from "../../utils/currencyId";
import { maxAmountSpend } from "../../utils/maxAmountSpend";
import {
  DynamicSection,
  MediumOnly,
  PageWrapper,
  ResponsiveTwoColumns,
  ScrollablePage,
  Wrapper
} from "./styled";
import { useMintActionHandlers } from "state/mint/hooks";
import { useDerivedMintInfo } from "state/mint/hooks";
import { useMintState } from "state/mint/hooks";

require('./style.css')

const DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE = new Percent(50, 10_000)

export default function Deposit({
  match: {
    params: { currencyIdA }
  },
  history
}: RouteComponentProps<{ currencyIdA?: string; }>) {
  const { account, chainId, library } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()
  const toggleWalletModal = useWalletModalToggle() // toggle wallet when disconnected
  // fee selection from url
  const currencyA = useCurrency(currencyIdA)

  // mint state
  const { independentField, typedValue } = useMintState();
  
  const {
    dependentField,
    currencies,
    currencyBalances,
    parsedAmounts,
    noLiquidity
  } = useDerivedMintInfo(undefined, currencyA ?? undefined, 'SELECT_POOL')
  const { onFieldAInput, onFieldBInput } =
    useMintActionHandlers(noLiquidity)

  const usdcValues = {
    [Field.CURRENCY_B]: useUSDCValue(parsedAmounts[Field.CURRENCY_B])
  }
  const assetVault = useAssetVaultContract()
  const [approval, approveCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_B],
    chainId ? ASSET_VAULT_ADDRESSES[chainId] : undefined
  )

  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm
  const [waiting, setWaiting] = useState<boolean>(false) // clicked confirm
  const [invalid, setInvalid] = useState<boolean>(false)
  const [insufficientBalance, setInsufficientBalance] = useState<boolean>(false)
  const [txHash, setTxHash] = useState<string>('')

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? ''
  }
  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field])
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0')
      }
    },
    {}
  )
  async function approveToken() {
    try {
      setWaiting(true);
      await approveCallback(); 
    } catch (error) {
      console.log(error);
    }
    setWaiting(false);
  }

  async function onDeposit() {
    // if(!clickable) return
    // setClickable(false)
    if (!chainId || !library || !account) return
    if (assetVault && account) {
      try {
        setWaiting(true)
        await assetVault.deposit(currencyIdA, (parsedAmounts[Field.CURRENCY_B])?.quotient.toString())
          .then(async (response: TransactionResponse) => {
            setTxHash(response.hash)
            addTransaction(response, {
              type: TransactionType.DEPOSIT_POSITION
            })
            setAttemptingTxn(false)
            setShowConfirm(true)
            await response.wait()
            setWaiting(false)
          })
          .catch((error: any) => {
            setWaiting(false)
            setAttemptingTxn(false)
            console.error('Failed to send transaction', error)
          })
      } catch (error) {
        setWaiting(false)
        console.error('Failed to send transaction', error)
        setAttemptingTxn(false)
        // we only care if the error is something _other_ than the user rejected the tx
        if (error?.code !== 4001) {
          console.error(error)
        }
      }
    }
  }

  const showConnectAWallet = Boolean(!account)

  const handleCurrencyBSelect = useCallback(
    (currencyANew: Currency) => {
      const idA = currencyId(currencyANew)
      history.push(`/lend/deposit/${idA}`)
    },
    [history]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
      // dont jump to pool page if creating
      history.push('/lend')
    }
    setTxHash('')
  }, [history, onFieldAInput, txHash])

  const clearAll = useCallback(() => {
    onFieldAInput('')
    onFieldBInput('')
    history.push(`/add`)
  }, [history, onFieldAInput, onFieldBInput])

  const onUserInput = (e: any) => {
    const decimal = currencies[Field.CURRENCY_B]?.decimals || 18;
    const removed = removeUnneededCharacter(e.toString())
    const splited = removed.split('.')
    if (splited.length > 1) {
      setInvalid(splited[1].length > decimal)
    } else {
      setInvalid(false)
    }
    // @ts-ignore
    setInsufficientBalance(parseFloat(e) > parseFloat(maxAmounts[Field.CURRENCY_B]?.toExact()) || parseFloat(e) < 0)
    onFieldBInput(removed)
  }

  const removeUnneededCharacter = (str: string) => {
    if (str.startsWith('0.')) {
      return str
    } else if (str.startsWith('00')) {
      return str.substring(2)
    } else if (str.length > 1 && str.startsWith('0')) {
      return str.substring(1)
    }
    return str
  }

  return (
    <>
      <ScrollablePage style={{ margin: '50px 0px 0px 0px' }}>
        <TransactionConfirmationModal
          isOpen={showConfirm}
          onDismiss={handleDismissConfirmation}
          attemptingTxn={attemptingTxn}
          hash={txHash}
          content={() => (
            <ConfirmationModalContent
              title={''}
              onDismiss={handleDismissConfirmation}
              bottomContent={() => (
                <button className='view-etherscan'>
                  <p>
                    View on Block Explorer
                  </p>
                </button>
              )}
            />
          )}
        />

        <div className='remove-tab-warrap-deposit'>
          <PageWrapper wide={true} className='remove-tab-deposit'>
            <AddRemoveTabs
              deposit={true}
              positionID={"0"}
              defaultSlippage={DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE}
              showBackLink={true}
            >
              {true && (
                <Row justifyContent='flex-end' style={{ width: 'fit-content', minWidth: 'fit-content' }}>
                  <MediumOnly>
                    <ButtonText onClick={clearAll} margin='0 15px 0 0'>
                      <ThemedText.Blue fontSize='12px'>
                        Clear All
                      </ThemedText.Blue>
                    </ButtonText>
                  </MediumOnly>
                </Row>
              )}
            </AddRemoveTabs>
            <Wrapper className='remove-tab-content' style={{ padding: 0 }}>
              <ResponsiveTwoColumns
                wide={true}
                style={{ display: 'block' }}
                className='remove-tab-content-inner'
              >
                <div>
                  <DynamicSection
                    disabled={false}
                    className='deposit-amount'
                  >
                    <AutoColumn gap='md'>
                      <div className='toToken' style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <CurrencyInputPanel
                          value={formattedAmounts[Field.CURRENCY_B]}
                          onUserInput={onUserInput}
                          onMax={() => {
                            onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                          }}
                          showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                          fiatValue={usdcValues[Field.CURRENCY_B]}
                          currency={currencies[Field.CURRENCY_B] ?? null}
                          onCurrencySelect={handleCurrencyBSelect}
                          disabled={waiting || approval === ApprovalState.PENDING}
                          id='add-liquidity-input-tokenb'
                          showCommonBases
                        />
                      </div>

                    </AutoColumn>
                  </DynamicSection>
                </div>


              </ResponsiveTwoColumns>
            </Wrapper>
            <div className='add-liquidity-footer'>
              {
                showConnectAWallet ? (
                  <button className='primary-button' onClick={toggleWalletModal}>
                    <p>Connect Wallet</p>
                  </button>
                ) : (

                  (invalid || insufficientBalance)
                    ?
                    <button className={invalid ? 'primary-button-invalid': 'primary-button'} disabled={true}>
                      <p>{invalid ? 'Invalid Amount' : 'Insufficient Balance'}</p>
                    </button>
                    :
                    approval === ApprovalState.NOT_APPROVED && !waiting
                      ?
                      <button className='buttonPrimary Approve-pair' style={{ border: '0px' }}
                              onClick={approveToken}>
                        Approve
                      </button>
                      :
                      (
                        (approval === ApprovalState.PENDING || waiting) && (approval !== ApprovalState.APPROVED)
                          ?
                          <button className='primary-button' style={{ border: '0px' }} disabled={true}>
                            <p style={{ color: 'white' }}>Approving ...</p>
                          </button>
                          :
                          <button className='primary-button' onClick={onDeposit} disabled={waiting}>
                            <p>Deposit</p>
                          </button>
                      )
                )
              }
            </div>
          </PageWrapper>
        </div>
      </ScrollablePage>
    </>
  )
}